import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Certifications from '../components/Certifications';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              About Us <span className="text-red-700">Asset</span> <span className="text-red-700">Wave</span>
            </h1>
            <p className="text-xl text-gray-600">
              Your trusted partner in financial security since 2009
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {/* Mission */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-700 leading-relaxed">
                At Asset Wave, we believe that every family deserves personalized financial advice 
                that puts their needs first—not sales targets or commissions. Our mission is to help 
                individuals and families achieve financial security through expert guidance, transparent 
                advice, and long-term partnership.
              </p>
            </section>

            {/* About K Satish */}
            <section className="mb-12 bg-blue-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet K Satish</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                With over <strong>20 years of experience</strong> in the insurance and financial services 
                industry, K Satish has helped over 1,000 families secure their financial future. 
                His approach is built on three core principles:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700"><strong>Client-First Approach:</strong> Understanding your unique needs before recommending any product</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700"><strong>Transparency:</strong> Clear explanations of all options, benefits, and costs</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700"><strong>Lifelong Partnership:</strong> Ongoing support for claims, renewals, and financial reviews</span>
                </li>
              </ul>
            </section>

            {/* Why Choose Us */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Us</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-blue-600 mb-2">Independent Advisory</h3>
                  <p className="text-gray-600">
                    We work with 10+ reputed insurance companies to find the best solution for YOU, 
                    not what pays us the highest commission.
                  </p>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-blue-600 mb-2">Proven Track Record</h3>
                  <p className="text-gray-600">
                    20+ years serving families, ₹100Cr+ coverage provided, and 1000+ satisfied clients.
                  </p>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-blue-600 mb-2">Comprehensive Services</h3>
                  <p className="text-gray-600">
                    From term insurance to retirement planning, we cover all aspects of your financial journey.
                  </p>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-blue-600 mb-2">Personal Touch</h3>
                  <p className="text-gray-600">
                    Direct access to your advisor. No call centers, no automated responses—just personalized service.
                  </p>
                </div>
              </div>
            </section>

            {/* Our Values */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8">
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">🎯</span>
                    <div>
                      <strong className="text-gray-900">Integrity:</strong>
                      <span className="text-gray-700"> Always recommend what's best for the client, not what's best for our commission</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">💡</span>
                    <div>
                      <strong className="text-gray-900">Education:</strong>
                      <span className="text-gray-700"> Empower clients with knowledge to make informed decisions</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">🤝</span>
                    <div>
                      <strong className="text-gray-900">Trust:</strong>
                      <span className="text-gray-700"> Build long-term relationships based on honesty and reliability</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-2xl mr-3">📞</span>
                    <div>
                      <strong className="text-gray-900">Accessibility:</strong>
                      <span className="text-gray-700"> Always available when you need us, especially during claims</span>
                    </div>
                  </li>
                </ul>
              </div>
            </section>

            {/* Official Licenses & Registrations */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Official Licenses & Registrations</h2>
              <p className="text-gray-700 mb-6">
                K Satish is officially licensed and registered with multiple reputed insurance companies and regulatory bodies, 
                ensuring you receive professional and compliant financial advice.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {/* Health Insurance License */}
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-emerald-600 rounded-lg p-2 mr-3">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg">Health Insurance</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-800">Care Health Insurance Ltd</p>
                    <p className="text-xs text-gray-600">License Code:</p>
                    <p className="text-sm font-mono bg-white px-3 py-2 rounded border border-emerald-200 text-emerald-800">
                      RLH20481559
                    </p>
                  </div>
                </div>

                {/* Life Insurance License */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-600 rounded-lg p-2 mr-3">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg">Life Insurance</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-800">SBI Life Insurance Company Ltd</p>
                    <p className="text-xs text-gray-600">License Code:</p>
                    <p className="text-sm font-mono bg-white px-3 py-2 rounded border border-blue-200 text-blue-800">
                      SLI991485554
                    </p>
                  </div>
                </div>

                {/* General Insurance License */}
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-orange-600 rounded-lg p-2 mr-3">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg">General Insurance</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-800">ICICI Lombard General Insurance Co. Ltd</p>
                    <p className="text-xs text-gray-600">License Code:</p>
                    <p className="text-sm font-mono bg-white px-3 py-2 rounded border border-orange-200 text-orange-800">
                      ILG70251
                    </p>
                  </div>
                </div>
              </div>

              {/* AMFI Registration */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-purple-600 rounded-lg p-3 flex-shrink-0">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-xl mb-2">AMFI Registered Mutual Fund Distributor</h3>
                    <p className="text-gray-700 mb-3">
                      Authorized to distribute mutual funds and provide investment advisory services
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">ARN Number:</p>
                        <p className="text-sm font-mono bg-white px-3 py-2 rounded border border-purple-200 text-purple-800">
                          349565
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Valid Period:</p>
                        <p className="text-sm font-semibold bg-white px-3 py-2 rounded border border-purple-200 text-purple-800">
                          30 Dec 2025 - 25 Dec 2028
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Certifications */}
            <section className="mb-12">
              <Certifications />
            </section>

            {/* CTA */}
            <section className="text-center bg-blue-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Ready to Secure Your Financial Future?
              </h2>
              <p className="text-blue-100 mb-6">
                Let's discuss your financial goals and create a personalized plan together.
              </p>
              <Link
                to="/#contact"
                className="inline-block bg-white text-blue-600 px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-lg font-semibold"
              >
                Schedule Free Consultation
              </Link>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
